import React, { useState } from 'react';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { CONTACT, IMAGES, LINKS } from '../data/site';
type Faq = {
  question: string;
  answer: React.ReactNode;
};
const FAQS: Faq[] = [{
  question: 'Can you help me choose metal shaping tools?',
  answer: <p data-id="element-87" data-mp="1gjbyvjzz090m">
        Yes. Tell us the projects you have in mind, the material you are working in and what equipment you
        already own, and we will point you to the tools that make sense for the work.{' '}
        <a className="underline decoration-steel-200 underline-offset-4 hover:decoration-fred" href={LINKS.contact} data-id="element-88" data-mp="28bb1uqbj4c2r">
          Contact the shop
        </a>{' '}
        for selection help, or browse{' '}
        <a className="underline decoration-steel-200 underline-offset-4 hover:decoration-fred" href={LINKS.shopAll} data-id="element-89" data-mp="1zhy51dacgqbe">
          the full catalog
        </a>
        .
      </p>
}, {
  question: 'How do I arrange a workshop?',
  answer: <p data-id="element-90" data-mp="2s0v9vyjslg9q">
        Workshops run on demand for small groups of one to five people. Get in touch to discuss your
        interests, experience and scheduling; tools and practice materials are provided.{' '}
        <a className="underline decoration-steel-200 underline-offset-4 hover:decoration-fred" href={LINKS.workshops} data-id="element-91" data-mp="10rm49f40i1h4">
          See workshop details
        </a>{' '}
        or ask about{' '}
        <a className="underline decoration-steel-200 underline-offset-4 hover:decoration-fred" href={LINKS.privateTraining} data-id="element-92" data-mp="19saf60y3zlps">
          private training
        </a>
        .
      </p>
}, {
  question: 'What should I send for a fabrication estimate?',
  answer: <p data-id="element-93" data-mp="1xhv91evsw3du">
        Include the material, thickness, dimensions, quantity and your timeline, plus any drawings or photos
        of the part or vehicle. Email them to{' '}
        <a className="break-words underline decoration-steel-200 underline-offset-4 hover:decoration-fred" href="mailto:jeff@fournierenterprises.com" data-id="element-94" data-mp="36gl93mcoe0q">
          jeff@fournierenterprises.com
        </a>
        , or read the{' '}
        <a className="underline decoration-steel-200 underline-offset-4 hover:decoration-fred" href={LINKS.faq} data-id="element-95" data-mp="1ybfy0pfnn0la">
          full FAQ
        </a>{' '}
        for more detail.
      </p>
}, {
  question: 'Do you ship internationally?',
  answer: <p data-id="element-96" data-mp="2c9nqol5nrfqn">
        Email{' '}
        <a className="underline decoration-steel-200 underline-offset-4 hover:decoration-fred" href={`mailto:${CONTACT.email}`} data-id="element-97" data-mp="2c6rr6se5wj03">
          {CONTACT.email}
        </a>{' '}
        with the items you want and your complete delivery address, and we will quote the shipping for your
        order.
      </p>
}];
export function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="border-t border-steel-200 bg-cream" aria-labelledby="faq-heading" data-id="element-98" data-mp="2zz8bl9ldgn2k">
      <div className="mx-auto grid max-w-[1240px] gap-x-10 gap-y-8 px-5 py-16 sm:px-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-x-16 lg:py-20" data-id="element-99" data-mp="34x0ainkfp9g9">
        <div data-id="element-100" data-mp="t83n620x64j3">
          <p className="font-display text-[13px] uppercase tracking-eyebrow text-fred" data-id="element-101" data-mp="ob3f5lkierpc">Questions</p>
          <h2 id="faq-heading" className="mt-4 font-display text-[36px] font-bold uppercase leading-[0.95] text-charcoal sm:text-[46px]" data-id="element-102" data-mp="k9i84gunp7n2">
            Metalworking questions,
            <br data-id="element-103" data-mp="1c0fxbaxtea3s" />
            answered.
          </h2>

          <img src={IMAGES.faqShop} width={640} height={480} loading="lazy" alt="Two people discussing shaped metal at a workbench." className="mt-6 h-auto w-full max-w-[320px] object-contain lg:max-w-[256px]" data-id="element-104" data-mp="1bzwfrvc2tnqa" />
        </div>

        <div className="border-t border-steel-200" data-id="element-105" data-mp="y915qfpud0l4">
          {FAQS.map((faq, index) => {
          const isOpen = openIndex === index;
          const panelId = `faq-panel-${index}`;
          const buttonId = `faq-button-${index}`;
          return <div key={faq.question} className="border-b border-steel-200" data-id="element-106" data-mp="1aheaynf9ir3j" data-mpi={index}>
                <h3 data-id="element-107" data-mp="1fi2176vo33wp">
                  <button type="button" id={buttonId} aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpenIndex(isOpen ? null : index)} className="flex min-h-[64px] w-full items-center justify-between gap-6 py-4 text-left font-display text-[22px] uppercase leading-tight text-charcoal transition-colors duration-150 ease-out hover:text-fred sm:text-[24px]" data-id="element-108" data-mp="1filhkj5o5k8m">
                    {faq.question}
                    {isOpen ? <MinusIcon aria-hidden="true" className="h-5 w-5 shrink-0 text-fred" data-id="element-109" data-mp="1pewxrk8dqu8c" /> : <PlusIcon aria-hidden="true" className="h-5 w-5 shrink-0 text-steel-400" data-id="element-110" data-mp="2d4ivs4d0n3er" />}
                  </button>
                </h3>
                <div id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen} className="max-w-[60ch] pb-6 text-[15px] leading-relaxed text-steel-600" data-id="element-111" data-mp="2rakx37h3i74d">
                  {faq.answer}
                </div>
              </div>;
        })}
        </div>
          <p className="border-t border-steel-200 pt-6 text-center text-[16px] leading-relaxed text-steel-600 lg:col-span-2" data-id="element-112" data-mp="28byt9ku721wr">
            If yours isn’t here, call{' '}
            <a href={CONTACT.phoneHref} className="font-medium text-charcoal underline decoration-steel-200 underline-offset-4 hover:decoration-fred" data-id="element-113" data-mp="24c6gsx1ojbm4">
              {CONTACT.phoneLabel}
            </a>{' '}
            or read the{' '}
            <a href={LINKS.faq} className="text-charcoal underline decoration-steel-200 underline-offset-4 hover:decoration-fred" data-id="element-114" data-mp="3to8iwqvzvzy8">
              full FAQ
            </a>
            .
          </p>
      </div>
    </section>;
}